/* eslint-disable @typescript-eslint/no-explicit-any */
import { WorldMapMarkerStory_worldMapView_markers } from 'graphql/query/__generated__/WorldMapMarkerStory';
import { useMarkerVisibilityView } from 'hooks/map/useMarkerVisibilityView';
import { mapGraphQLMarkers } from 'libs/utils/mapping/worldGrahQLMaps';
import { useAnalytics } from 'libs/utils/site/analytics';
import _ from 'lodash';
import { SeafoodButtons } from 'pages/custom/Seafood/SeafoodButtons';
import { CustomMapPage } from 'pages/custom/shared/CustomMapPage';
import { OverflowFilters } from 'pages/custom/shared/overflow/OverflowFilters';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

const EAT_SET = 'caa103a6-e3a0-463f-8d29-7beda677e902';
const EXPLORE_SET = 'e890ea14-d962-488d-ae8d-01c0b1c06b45';
const EXPERIENCE_SET = '580a1797-1d07-4c60-b78b-b5af2cf709ab';
const LEARN_SET = 'cf50bb8d-a89b-4678-ab6d-2d50d4b0d26e';

const GROUP_MARKER_1 = 'edc86ac4-a3ee-44b0-9e6e-8519b59f7d7f';
const GROUP_MARKER_2 = '9282c9ad-988d-49ae-94de-aaecc2c7bc32';
const GROUP_MARKERS = [GROUP_MARKER_1, GROUP_MARKER_2];
const GROUP_TO_STORIES: Record<string, string[]> = {
    [GROUP_MARKER_1]: [
        'd75995e8-2c0b-4428-b87d-0c64345bcf9d',
        '190b41c9-bc90-4486-929c-95b92d07a129',
        '2e3ea39f-9a80-4223-b949-35b663a93dc7',
        'b5ed342c-3bd2-45be-a799-38508650b001',
        '2a745a9f-146c-49a6-81bb-00a68937fe0c',
        'f6f5d24a-b7dc-4da2-9ea5-595eaeb706c5',
        'a3db2a60-9ce2-41e6-a069-21a7391b6d48',
        '583e3d20-cb25-4004-97d3-33e133212f91',
        '319a281d-48cc-486e-9391-214e376aedc5',
        '690ecff4-1177-4af7-b53d-e08f86572881',
        '500372f2-fa41-4063-8404-4e58704e693b',
        'e76adf60-0682-40ee-b46e-c0b0333e124e',
        'e7356e69-7415-4f00-bd1d-c3791c85b866',
        '6a4809ec-51c4-4150-9e29-43659c6133e3',
        'cab07b2e-490e-4319-b53b-39915dc49549',
        '0af79f0b-2827-43e6-b89d-7a68ebde5f0f',
        'ae8c2ffc-4280-43df-88a1-b6fe99df0e7c'
    ],
    [GROUP_MARKER_2]: [
        '090adf00-e414-45e0-ae4c-b82643de6fc7',
        '97093dee-401b-4403-ba3f-9327f5de2194',
        '6da8388b-9742-4b5b-a343-6751a51df928',
        '669bc40c-bfa1-4cba-99c0-2d7016753e98',
        'e76adf60-0682-40ee-b46e-c0b0333e124e',
        'e7356e69-7415-4f00-bd1d-c3791c85b866',
        'd838a7be-0260-46a4-bdea-cf26ee066fb7',
        '260ed3cc-aa28-42bd-a2ca-99b4c78295c3',
        '2e3ea39f-9a80-4223-b949-35b663a93dc7',
        'dcbc7e49-887c-48a7-994b-ae5fed86dcb9',
        '2c301d3d-8fb4-46ef-9731-04cdd7d2f46a',
        '690ecff4-1177-4af7-b53d-e08f86572881',
        '583e3d20-cb25-4004-97d3-33e133212f91',
        'b6c1f157-7a04-49a8-a27f-135128e54a18',
        'a8eca5aa-bbe1-4a1a-999a-0ab3125b333b',
        'aac04ad6-eada-4032-82bd-f31d831d0ff0'
    ]
};

export const SeafoodENPage = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const markerVisibility = useMarkerVisibilityView();
    const analytics = useAnalytics();

    function setAllVisible() {
        ctxModal.setShowListOfStories(false);
        ctxView.setVisibleMapSetIds([EAT_SET, EXPLORE_SET, EXPERIENCE_SET, LEARN_SET]);
        analytics.track('custom_button.clicked', {
            action: 'filter.reset'
        });
    }

    function setVisibleMapSet(mapSetId: string) {
        ctxModal.setShowListOfStories(false);
        ctxView.setVisibleMapSetIds([mapSetId]);
        analytics.track('custom_button.clicked', {
            action: 'filter.select',
            categoryId: mapSetId
        });
    }

    function isSetHighlighted(mapSetId: string) {
        return ctxView.visibleMapSetIds.length === 1 && ctxView.visibleMapSetIds[0] === mapSetId;
    }

    function modifyMarkers(markers: WorldMapMarkerStory_worldMapView_markers[]) {
        const updatedMarkers = _.clone(markers);
        markers.forEach((marker, index) => {
            if (marker.worldMapSets.length >= 2) {
                if (ctxView.visibleMapSetIds?.length === 1 && marker.type === 'PIN') {
                    if (checkIfProperSet(marker, EAT_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    backgroundColor: '569AB5',
                                    iconColor: 'ffffff',
                                    iconText: 'utensils'
                                }
                            } as any
                        };
                    } else if (checkIfProperSet(marker, EXPLORE_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    backgroundColor: '254765',
                                    iconColor: 'ffffff',
                                    iconText: 'binoculars'
                                }
                            } as any
                        };
                    } else if (checkIfProperSet(marker, EXPERIENCE_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    backgroundColor: 'A2C034',
                                    iconColor: 'ffffff',
                                    iconText: 'sparkles'
                                }
                            } as any
                        };
                    } else if (checkIfProperSet(marker, LEARN_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    ...(marker.data['icon'] as any),
                                    backgroundColor: '5FAA9B',
                                    iconColor: 'ffffff',
                                    iconText: 'graduation-cap'
                                } as any
                            } as any
                        };
                    }
                }
            }
        });

        return updatedMarkers ? mapGraphQLMarkers(updatedMarkers) : [];

        function checkIfProperSet(marker: WorldMapMarkerStory_worldMapView_markers, setId: string) {
            return (
                ctxView.visibleMapSetIds?.includes(setId) &&
                marker.worldMapSets?.find((set) => set.worldMapSetId === setId)
            );
        }
    }

    const preliminaryMarkers = (
        ctxView.allMarkers ?? []
    ).map<WorldMapMarkerStory_worldMapView_markers>((marker) => ({
        ...marker,
        selectable:
            markerVisibility.isMarkerSelectableInView(marker) ||
            GROUP_MARKERS.includes(marker.worldMapMarkerId),
        visible:
            markerVisibility.isMarkerVisibleInViewMap(marker) ||
            GROUP_MARKERS.includes(marker.worldMapMarkerId)
    }));

    // Go through the preliminary ones and hide Group markers that don't have any children visible
    const markers = preliminaryMarkers.map((marker) => {
        if (GROUP_MARKERS.includes(marker.worldMapMarkerId)) {
            const childMarkers = preliminaryMarkers.filter((e) =>
                GROUP_TO_STORIES[marker.worldMapMarkerId].includes(e.story?.storyId)
            );
            return {
                ...marker,
                visible: childMarkers.some((e) =>
                    markerVisibility.isMarkerStoryVisibleInViewList(e)
                )
            };
        }
        return marker;
    });

    const modifiedMarkers: IWorldMarker[] = modifyMarkers(markers);

    function openContent(ids: string[]) {
        const storyIdsToShow = markers
            .filter(
                (marker) =>
                    markerVisibility.isMarkerStoryVisibleInViewList(marker) &&
                    ids.includes(marker.story?.storyId)
            )
            .map((e) => e.story?.storyId);
        ctxModal.setVisibleStoryIds(storyIdsToShow);
        ctxModal.setShowListOfStories(true);
    }

    return (
        <S_SeafoodEnPage className={props.className}>
            <CustomMapPage
                mapId={'30c56d72-b6ca-497c-b555-10ddeb5fc9e6'}
                header={{
                    logo: 'https://norwayseafoodfestival.no/wp-content/themes/norseafoodfest/images/logo-landscape-red.svg',
                    link: 'https://norwayseafoodfestival.no/en/frontpage/',
                    linkText: Localization.WEBSITE,
                    logoMarginTop: '-11px'
                }}
                controls={<SeafoodButtons />}
                overrideMapMarkers={modifiedMarkers ?? []}
                onMarkerSelected={(markerId: string) => {
                    if (markerId === GROUP_MARKER_1 || markerId === GROUP_MARKER_2) {
                        openContent(GROUP_TO_STORIES[markerId]);
                        return true;
                    }
                }}
                topOverflow={
                    <OverflowFilters
                        highlightedBackground="linear-gradient(270deg, rgb(173 64 23) 0%, rgb(200 102 65) 100%)"
                        items={[
                            {
                                iconName: 'location-dot',
                                text: Localization.ALL,
                                onClick: setAllVisible,
                                highlighted: ctxView.visibleMapSetIds.length === 4
                            },
                            {
                                iconName: 'utensils',
                                text: Localization.EAT,
                                onClick: () => {
                                    setVisibleMapSet(EAT_SET);
                                },
                                highlighted: isSetHighlighted(EAT_SET)
                            },
                            {
                                iconName: 'binoculars',
                                text: Localization.EXPLORE,
                                onClick: () => {
                                    setVisibleMapSet(EXPLORE_SET);
                                },
                                highlighted: isSetHighlighted(EXPLORE_SET)
                            },
                            {
                                iconName: 'sparkles',
                                text: Localization.EXPERIENCE,
                                onClick: () => {
                                    setVisibleMapSet(EXPERIENCE_SET);
                                },
                                highlighted: isSetHighlighted(EXPERIENCE_SET)
                            },
                            {
                                iconName: 'graduation-cap',
                                text: Localization.LEARN,
                                onClick: () => {
                                    setVisibleMapSet(LEARN_SET);
                                },
                                highlighted: isSetHighlighted(LEARN_SET)
                            }
                        ]}
                    />
                }
            />
        </S_SeafoodEnPage>
    );
};

const S_SeafoodEnPage = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
`;
